import { RefreshCw } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import citriniService from "../../../../services/citriniService";
import { CitriniFetchInfoItem, PagedResponse } from "../../../../types/citrini";
import {
  EmptyState,
  ErrorBanner,
  formatDateCell,
  formatDuration,
  LoadingSpinner,
  Pagination,
} from "../helpers";
import FetchStatusBadge from "./FetchStatusBadge";

interface FetchInfoTableProps {
  isDarkMode: boolean;
}

const FetchInfoTable: React.FC<FetchInfoTableProps> = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<PagedResponse<CitriniFetchInfoItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async (p: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await citriniService.listFetchInfo({ page: p, pageSize: 20, orderDir: "desc" });
      setData(res);
      setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load fetch info");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  if (loading && !data) {
    return <LoadingSpinner isDarkMode={isDarkMode} />;
  }

  if (error && !data) {
    return <ErrorBanner message={error} onRetry={() => load(1)} isDarkMode={isDarkMode} />;
  }

  if (!data || data.items.length === 0) {
    return <EmptyState message="No fetch info records found" isDarkMode={isDarkMode} />;
  }

  return (
    <div>
      <div className="flex items-center justify-end mb-3">
        <button
          onClick={() => load(page)}
          disabled={loading}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border flex items-center ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised disabled:opacity-50"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className={`text-left text-xs ${
                isDarkMode
                  ? "text-rp-grey/50 border-b border-rp-separator"
                  : "text-gray-500 border-b border-gray-200"
              }`}
            >
              <th className="pb-2 pr-4 font-medium">ID</th>
              <th className="pb-2 pr-4 font-medium">Started At (UTC)</th>
              <th className="pb-2 pr-4 font-medium">Finished At (UTC)</th>
              <th className="pb-2 pr-4 font-medium">Duration</th>
              <th className="pb-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            {data.items.map((item) => (
              <tr
                key={item.id}
                onClick={() =>
                  navigate(`/admin/citrini-import/${item.id}`, { state: { fetchInfo: item } })
                }
                className={`cursor-pointer ${
                  isDarkMode ? "hover:bg-rp-surface/50" : "hover:bg-gray-50"
                }`}
              >
                <td
                  className={`py-2.5 pr-4 font-mono text-xs ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                  }`}
                >
                  {item.id}
                </td>
                <td className={`py-2.5 pr-4 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                  {formatDateCell(item.startedAtUtc)}
                </td>
                <td className={`py-2.5 pr-4 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                  {formatDateCell(item.finishedAtUtc)}
                </td>
                <td className={`py-2.5 pr-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
                  {formatDuration(item.startedAtUtc, item.finishedAtUtc)}
                </td>
                <td className="py-2.5">
                  <FetchStatusBadge
                    startedAtUtc={item.startedAtUtc}
                    finishedAtUtc={item.finishedAtUtc}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={page}
        totalPages={data.totalPages}
        totalItems={data.totalItems}
        onPageChange={load}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default FetchInfoTable;
